import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { cartAdd } from '@theme/na-cart-api';
import { closeFreeProductPopup } from '@theme/na-cart-free-gifts';

/** @type {Set<string>} */
const autoAddedVariants = new Set();

/** Concurrent /cart/add.js calls can drop lines, so auto gifts are added one at a time. */
let autoAddQueue = Promise.resolve();

// Attempts are released once the cart drops back to (or below) the gift count they started from,
// so a pruned gift can be added again after a color swap or a quantity change.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const items = /** @type {{ detail?: { resource?: { items?: Array<Record<string, any>> } } }} */ (event)?.detail
        ?.resource?.items;
    if (!Array.isArray(items)) return;
    /** @type {Record<string, number>} */
    const giftCounts = {};
    for (const line of items) {
        if (line.properties?._free_product !== 'true') continue;
        const prefix = `${line.variant_id}:${line.properties?._main_variant_id ?? ''}:`;
        giftCounts[prefix] = (giftCounts[prefix] || 0) + (line.quantity || 0);
    }
    for (const attemptKey of autoAddedVariants) {
        const splitAt = attemptKey.lastIndexOf(':') + 1;
        const startedAt = parseInt(attemptKey.slice(splitAt), 10) || 0;
        if ((giftCounts[attemptKey.slice(0, splitAt)] || 0) <= startedAt) autoAddedVariants.delete(attemptKey);
    }
});

/**
 * @typedef {{ buttonText: HTMLElement, buttonSpinner: HTMLElement }} NaFreeProductAddRefs
 */

/** @extends {Component<NaFreeProductAddRefs>} */
class NaFreeProductAddComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['buttonText', 'buttonSpinner'];

    connectedCallback() {
        super.connectedCallback();
        const item = this.closest('.js-na-free-product-item');
        if (item?.getAttribute('data-auto-add') === 'true') {
            void this.#autoAdd(item);
        }
    }

    /**
     * @param {Event} event
     */
    async add(event) {
        event.preventDefault();
        if (this.getAttribute('data-disabled') === 'true') return;
        await this.#addFromItem(this.closest('.js-na-free-product-item'), false);
    }

    /**
     * @param {Element | null} item
     */
    async #autoAdd(item) {
        const variantId = this.getAttribute('data-current-variant');
        const dataset = /** @type {HTMLElement | null} */ (item)?.dataset;
        const attemptKey = `${variantId}:${dataset?.mainVariantId ?? ''}:${dataset?.linkedGifts ?? '0'}`;
        if (!variantId || autoAddedVariants.has(attemptKey)) return;
        autoAddedVariants.add(attemptKey);
        item?.setAttribute('data-auto-add-fired', 'true');
        autoAddQueue = autoAddQueue.then(async () => {
            if (!(await this.#addFromItem(item, true))) autoAddedVariants.delete(attemptKey);
        });
        await autoAddQueue;
    }

    /**
     * @param {Element | null} item
     * @param {boolean} isAutoAdd
     * @returns {Promise<boolean>}
     */
    async #addFromItem(item, isAutoAdd) {
        const variantId = this.getAttribute('data-current-variant');
        if (!variantId) return false;

        const wrapper = this.closest('.js-na-free-product-wrapper');
        if (!isAutoAdd) {
            this.classList.add('na-free-product__button--loading');
            wrapper?.classList.add('na-free-product__wrapper--loading');
        }

        const dataset = /** @type {HTMLElement | null} */ (item)?.dataset;
        const quantity = isAutoAdd ? Math.max(parseInt(dataset?.autoAddQuantity ?? '1', 10) || 1, 1) : 1;
        /** @type {{ id: string, quantity: number, properties?: Record<string, string> }} */
        const itemToAdd = { id: variantId, quantity };
        if (dataset?.productId) {
            itemToAdd.properties = { _free_product: 'true' };
            if (dataset.mainProductId) itemToAdd.properties._main_product_id = dataset.mainProductId;
            if (isAutoAdd && dataset.mainVariantId) itemToAdd.properties._main_variant_id = dataset.mainVariantId;
        }

        try {
            await cartAdd([itemToAdd], isAutoAdd ? { giftIntent: 'adding' } : undefined);
            closeFreeProductPopup();
            const drawer = document.querySelector('cart-drawer-component');
            if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function') {
                /** @type {any} */ (drawer).open();
            }
            return true;
        } catch (error) {
            console.error('[na-free-product-add] /cart/add.js failed', error);
            return false;
        } finally {
            this.classList.remove('na-free-product__button--loading');
            wrapper?.classList.remove('na-free-product__wrapper--loading');
        }
    }
}

/**
 * @param {Element | null} item
 * @param {{ variantId?: string, variantImage?: string, variantTitle?: string, comparePrice?: string }} selection
 */
function applyFreeProductSelection(item, selection) {
    if (!item) return;
    const addButton = item.querySelector('na-free-product-add-component');
    if (addButton && selection.variantId) {
        addButton.setAttribute('data-current-variant', selection.variantId);
        const addToCartText = addButton.getAttribute('data-add-text') || 'Add to Cart';
        const buttonText = addButton.querySelector('.js-na-free-product-button-text');
        if (buttonText) buttonText.textContent = addToCartText;
    }

    const image = /** @type {HTMLImageElement | null} */ (item.querySelector('.js-na-free-product-image img'));
    if (image && selection.variantImage && selection.variantImage !== 'null') {
        const src = selection.variantImage.startsWith('//')
            ? `https:${selection.variantImage}`
            : selection.variantImage;
        image.src = src;
        image.srcset = `${src} 200w`;
    }

    if (selection.comparePrice) {
        const priceOriginal = item.querySelector('.js-na-free-product-price-original');
        if (priceOriginal) priceOriginal.textContent = selection.comparePrice;
    }

    if (selection.variantTitle) {
        const colorLabel = item.querySelector('.js-na-free-product-swatch-label');
        if (colorLabel) colorLabel.textContent = selection.variantTitle;
    }
}

class NaFreeProductItemComponent extends Component {
    /**
     * @param {Event} event
     */
    onSwatchChange(event) {
        const input = /** @type {HTMLInputElement} */ (event.target);
        if (!input.checked) return;
        applyFreeProductSelection(this, {
            variantId: input.dataset.variantId,
            variantImage: input.dataset.variantImage,
            variantTitle: input.dataset.variantTitle,
            comparePrice: input.dataset.variantComparePrice,
        });
    }

    /**
     * @param {Event} event
     */
    onVariantSelect(event) {
        const select = /** @type {HTMLSelectElement} */ (event.target);
        const option = select.options[select.selectedIndex];
        applyFreeProductSelection(this, {
            variantId: select.value,
            variantImage: option?.getAttribute('data-img') ?? '',
            comparePrice: option?.getAttribute('data-compare-price') ?? '',
        });
    }
}

if (!customElements.get('na-free-product-add-component')) {
    customElements.define('na-free-product-add-component', NaFreeProductAddComponent);
}

if (!customElements.get('na-free-product-item-component')) {
    customElements.define('na-free-product-item-component', NaFreeProductItemComponent);
}
